import React, { useState, useEffect, useCallback } from 'react';
import {
  Users, Eye, CreditCard, CheckCircle, Crown, ArrowDown,
  TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight,
  Loader2, AlertTriangle, Info, Sparkles, DollarSign,
  BarChart3, RefreshCw, Target, Repeat
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '365d', days: 365 },
];

const GrowthBadge = ({ pct }) => {
  if (pct === 0 || pct === undefined || pct === null) return <span className="text-[10px] text-stone-400">—</span>;
  const up = pct > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[11px] font-semibold ${up ? 'text-emerald-600' : 'text-rose-500'}`}>
      <Icon className="w-3 h-3" />{Math.abs(pct)}%
    </span>
  );
};

const FunnelStepRow = ({ icon: Icon, label, users, rateFromPrev, rateFromTop, dropOff, isFirst, isLast, maxUsers }) => {
  const barWidth = maxUsers > 0 ? Math.max((users / maxUsers) * 100, 3) : 3;
  return (
    <div data-testid={`sf-step-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-stone-900 flex items-center justify-center flex-shrink-0">
          <Icon className="w-3.5 h-3.5 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-semibold text-stone-700">{label}</span>
            <div className="flex items-center gap-2">
              <span className="text-sm font-serif font-bold text-stone-900">{users.toLocaleString()}</span>
              {!isFirst && (
                <span className="text-[10px] text-stone-400">({rateFromTop}% of top)</span>
              )}
            </div>
          </div>
          <div className="h-2.5 bg-stone-50 rounded-full overflow-hidden">
            <div className="h-full bg-stone-800 rounded-full transition-all duration-700" style={{ width: `${barWidth}%` }} />
          </div>
        </div>
      </div>
      {!isLast && dropOff > 0 && (
        <div className="flex items-center gap-2 ml-11 my-1.5">
          <ArrowDown className="w-3 h-3 text-rose-400" />
          <span className="text-[10px] text-rose-500 font-medium">-{dropOff}% drop-off</span>
          <span className="text-[10px] text-stone-400 ml-auto">{rateFromPrev}% conversion</span>
        </div>
      )}
    </div>
  );
};

const PlanCard = ({ plan, label, testId }) => {
  const hasData = plan.checkout_started > 0 || plan.completed > 0;
  const revUsd = plan.revenue?.usd?.revenue || 0;
  const revNok = plan.revenue?.nok?.revenue || 0;
  const ordersUsd = plan.revenue?.usd?.count || 0;
  const ordersNok = plan.revenue?.nok?.count || 0;

  return (
    <div data-testid={testId} className="bg-white border border-stone-100 rounded-xl p-4">
      <h4 className="text-xs font-semibold text-stone-600 uppercase tracking-wider mb-3">{label}</h4>
      {hasData ? (
        <>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div>
              <p className="text-[10px] text-stone-400">Checkout started</p>
              <p className="text-base font-serif font-bold text-stone-800">{plan.checkout_started}</p>
            </div>
            <div>
              <p className="text-[10px] text-stone-400">Completed</p>
              <p className="text-base font-serif font-bold text-stone-800">{plan.completed}</p>
            </div>
          </div>
          <div className="flex items-center justify-between py-2 border-t border-stone-50">
            <span className="text-[10px] text-stone-400">Conversion</span>
            <span className="text-xs font-semibold text-stone-700">{plan.conversion}%</span>
          </div>
          <div className="flex items-center justify-between py-2 border-t border-stone-50">
            <span className="text-[10px] text-stone-400">Revenue (USD)</span>
            <span className="text-xs font-semibold text-stone-700">${revUsd.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>
          {revNok > 0 && (
            <div className="flex items-center justify-between py-2 border-t border-stone-50">
              <span className="text-[10px] text-stone-400">Revenue (NOK)</span>
              <span className="text-xs font-semibold text-stone-700">{revNok.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kr</span>
            </div>
          )}
          <div className="flex items-center justify-between py-2 border-t border-stone-50">
            <span className="text-[10px] text-stone-400">Orders</span>
            <span className="text-xs text-stone-500">{ordersUsd + ordersNok}</span>
          </div>
        </>
      ) : (
        <div className="text-center py-4">
          <p className="text-xs text-stone-400">No {label.toLowerCase()} checkouts in this period</p>
        </div>
      )}
    </div>
  );
};

const InsightCard = ({ insight }) => {
  const config = {
    positive: { icon: CheckCircle, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100' },
    warning: { icon: AlertTriangle, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100' },
    info: { icon: Info, color: 'text-stone-500', bg: 'bg-stone-50', border: 'border-stone-100' },
  }[insight.type] || { icon: Info, color: 'text-stone-500', bg: 'bg-stone-50', border: 'border-stone-100' };
  const Icon = config.icon;
  return (
    <div data-testid={`sf-insight-${insight.type}`} className={`flex gap-3 p-3 rounded-lg border ${config.bg} ${config.border}`}>
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${config.color}`} />
      <div>
        <p className="text-xs font-semibold text-stone-700">{insight.title}</p>
        <p className="text-[11px] text-stone-500 mt-0.5">{insight.detail}</p>
      </div>
    </div>
  );
};

const FUNNEL_ICONS = [Users, Eye, CreditCard, CheckCircle, Crown];

export const SubscriptionFunnelSection = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/subscription-funnel?days=${d}`);
      setData(res.data);
    } catch (err) {
      console.error('Subscription funnel error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(days); }, [days, fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-stone-400" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-10">
        <p className="text-xs text-stone-400">Failed to load subscription funnel</p>
      </div>
    );
  }

  const { funnel, plans, summary: s, growth: g, insights } = data;
  const maxUsers = Math.max(...funnel.map(f => f.unique_users), 1);
  const hasActivity = s.pricing_viewed > 0 || s.checkout_started > 0 || s.completed > 0;

  return (
    <div data-testid="subscription-funnel-section" className="space-y-8 overflow-hidden">
      {/* Header + period selector */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-serif font-bold text-stone-800">Subscription Funnel</h2>
          <p className="text-[11px] text-stone-400 mt-0.5">Free → Pricing → Checkout → Pro conversion</p>
        </div>
        <div className="flex items-center gap-1 bg-stone-50 rounded-lg p-1">
          {PERIODS.map(p => (
            <button
              key={p.days}
              data-testid={`sf-period-${p.days}`}
              onClick={() => setDays(p.days)}
              className={`px-2.5 py-1.5 text-[10px] font-semibold rounded-md transition-all ${days === p.days ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-400 hover:text-stone-600'}`}
            >
              {p.label}
            </button>
          ))}
          <button onClick={() => fetchData(days)} className="ml-1 p-1.5 text-stone-400 hover:text-stone-600" data-testid="sf-refresh">
            <RefreshCw className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
        <div data-testid="sf-kpi-free" className="bg-white border border-stone-100 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Users className="w-3 h-3 text-stone-400" />
            <span className="text-[10px] text-stone-400 uppercase tracking-wider">Free Users</span>
          </div>
          <span className="text-base font-serif font-bold text-stone-800">{s.total_free.toLocaleString()}</span>
        </div>
        <div data-testid="sf-kpi-pricing" className="bg-white border border-stone-100 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Eye className="w-3 h-3 text-stone-400" />
            <span className="text-[10px] text-stone-400 uppercase tracking-wider">Viewed Pricing</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-base font-serif font-bold text-stone-800">{s.pricing_viewed}</span>
            <GrowthBadge pct={g.pricing_viewed.pct} />
          </div>
        </div>
        <div data-testid="sf-kpi-checkout" className="bg-white border border-stone-100 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <CreditCard className="w-3 h-3 text-stone-400" />
            <span className="text-[10px] text-stone-400 uppercase tracking-wider">Started Checkout</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-base font-serif font-bold text-stone-800">{s.checkout_started}</span>
            <GrowthBadge pct={g.checkout_started.pct} />
          </div>
        </div>
        <div data-testid="sf-kpi-completed" className="bg-white border border-stone-100 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <CheckCircle className="w-3 h-3 text-stone-400" />
            <span className="text-[10px] text-stone-400 uppercase tracking-wider">Completed</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-base font-serif font-bold text-stone-800">{s.completed}</span>
            <GrowthBadge pct={g.completed.pct} />
          </div>
        </div>
        <div data-testid="sf-kpi-active" className="bg-white border border-stone-100 rounded-xl p-3">
          <div className="flex items-center gap-1.5 mb-1">
            <Crown className="w-3 h-3 text-stone-400" />
            <span className="text-[10px] text-stone-400 uppercase tracking-wider">Active Pro</span>
          </div>
          <span className="text-base font-serif font-bold text-stone-800">{s.active_pro}</span>
          {s.cancelling > 0 && <p className="text-[10px] text-amber-500 mt-0.5">{s.cancelling} cancelling</p>}
        </div>
      </div>

      {/* Funnel visualization */}
      <div className="bg-white border border-stone-100 rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 data-testid="sf-funnel-heading" className="text-sm font-semibold text-stone-600 uppercase tracking-wider">Conversion Funnel</h3>
          <span className="text-[10px] text-stone-400">
            Overall: {s.overall_conversion}% (Pricing → Pro)
          </span>
        </div>

        {hasActivity ? (
          <div className="space-y-1.5">
            {funnel.map((step, i) => (
              <FunnelStepRow
                key={step.step}
                icon={FUNNEL_ICONS[i] || Users}
                label={step.step}
                users={step.unique_users}
                rateFromPrev={step.rate_from_prev}
                rateFromTop={step.rate_from_top}
                dropOff={step.drop_off}
                isFirst={i === 0}
                isLast={i === funnel.length - 1}
                maxUsers={maxUsers}
              />
            ))}
          </div>
        ) : (
          <div data-testid="sf-empty-state" className="flex flex-col items-center justify-center py-10 text-center">
            <div className="w-12 h-12 rounded-full bg-stone-50 flex items-center justify-center mb-3">
              <BarChart3 className="w-5 h-5 text-stone-300" />
            </div>
            <h4 className="text-sm font-serif font-semibold text-stone-600 mb-1">No subscription activity yet</h4>
            <p className="text-[11px] text-stone-400 max-w-xs">No users have viewed the pricing page or started a subscription checkout in this period. Try expanding the time range.</p>
          </div>
        )}
      </div>

      {/* Monthly vs Yearly */}
      <div>
        <h3 data-testid="sf-plans-heading" className="text-sm font-semibold text-stone-600 uppercase tracking-wider mb-3">Monthly vs Yearly</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <PlanCard plan={plans.monthly} label="Monthly ($19.99/mo)" testId="sf-plan-monthly" />
          <PlanCard plan={plans.yearly} label="Yearly ($149/yr)" testId="sf-plan-yearly" />
        </div>
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div>
          <h3 data-testid="sf-insights-heading" className="text-sm font-semibold text-stone-600 uppercase tracking-wider mb-3">Insights</h3>
          <div className="space-y-2">
            {insights.map((ins, i) => <InsightCard key={i} insight={ins} />)}
          </div>
        </div>
      )}

      {/* Footer note */}
      <p className="text-[10px] text-stone-400 text-center">
        Unique users per step. Renewals not counted as new conversions. NOK/USD shown separately. Only completed payments counted.
      </p>
    </div>
  );
};
